import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { userApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    AllInternalRoles,
    AllOrganizationalRoles,
    Role,
} from '@pillar-portal/database/dist/frontend';

interface ActivationUrlCardProps {
    invitationToken: string;
    userUuid?: string;
    email?: string;
    userRoles?: Role[]; // Optional: if not provided, email sending will be disabled
    title?: string;
    description?: string;
    showToken?: boolean; // Show token separately (for user profile case)
    emailSent?: boolean;
    emailError?: Error | null;
    onEmailSent?: () => void;
    actionButtons?: React.ReactNode;
    className?: string;
}

// Helper function to determine if email sending should be enabled
function shouldEnableEmailSending(userRoles?: Role[]): boolean {
    if (!userRoles || userRoles.length === 0) {
        return false;
    }

    // Enable email sending for internal and organizational users
    return userRoles.some(
        (role) => AllInternalRoles.includes(role) || AllOrganizationalRoles.includes(role)
    );
}

export function ActivationUrlCard({
    invitationToken,
    userUuid,
    email,
    userRoles,
    title = 'Activation URL',
    description,
    showToken = false,
    emailSent = false,
    emailError,
    onEmailSent,
    actionButtons,
    className = 'max-w-2xl',
}: ActivationUrlCardProps) {
    const log = createComponentLogger('ActivationUrlCard');
    const [showSendEmailConfirm, setShowSendEmailConfirm] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    // Track only *this component's* send attempts; `emailSent` represents external knowledge.
    const [localEmailSent, setLocalEmailSent] = useState(false);
    const [localEmailError, setLocalEmailError] = useState<Error | null>(emailError || null);
    const activationUrl = invitationToken
        ? `${window.location.origin}/verify-email/${invitationToken}`
        : '';

    const enableEmailSending = shouldEnableEmailSending(userRoles);

    const handleCopyUrl = () => {
        if (activationUrl) {
            runInBackground(navigator.clipboard.writeText(activationUrl), (err) => {
                log.error('Error copying URL to clipboard', err);
            });
        }
    };

    const handleCopyToken = () => {
        if (invitationToken) {
            runInBackground(navigator.clipboard.writeText(invitationToken), (err) => {
                log.error('Error copying token to clipboard', err);
            });
        }
    };

    const handleSendEmail = () => {
        setShowSendEmailConfirm(true);
    };

    const handleConfirmSendEmail = async () => {
        if (!enableEmailSending || !email || userUuid === undefined || userUuid.length === 0) {
            return;
        }

        setIsSendingEmail(true);
        setLocalEmailError(null);

        try {
            await userApi.sendVerificationEmailAdmin(userUuid);
            setLocalEmailSent(true);
            setShowSendEmailConfirm(false);
            if (onEmailSent) {
                onEmailSent();
            }
        } catch (error) {
            setLocalEmailError(error instanceof Error ? error : new Error('Failed to send email'));
        } finally {
            setIsSendingEmail(false);
        }
    };

    const effectiveEmailSent = emailSent || localEmailSent;
    const effectiveEmailError = emailError || localEmailError;
    const sendEmailVerb: string = effectiveEmailSent ? 'resend' : 'send';

    return (
        <>
            <Card className={className}>
                <CardHeader>
                    <CardTitle>{title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {localEmailSent && email && (
                        <Alert>
                            <AlertDescription>
                                Account activation email has been sent successfully to {email}.
                            </AlertDescription>
                        </Alert>
                    )}
                    {!localEmailSent && emailSent && email && (
                        <Alert>
                            <AlertDescription>
                                Account activation email was sent automatically during account
                                creation to {email}.
                            </AlertDescription>
                        </Alert>
                    )}
                    {effectiveEmailError && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {effectiveEmailError instanceof Error
                                    ? effectiveEmailError.message
                                    : 'Failed to send verification email'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {description && (
                        <Alert>
                            <AlertDescription>{description}</AlertDescription>
                        </Alert>
                    )}

                    <div className="space-y-2">
                        <Label>Activation URL</Label>
                        <p className="text-sm text-muted-foreground">
                            Send this URL to the user to complete their account activation
                        </p>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={handleCopyUrl}
                                title="Copy to clipboard"
                            >
                                ⧉
                            </Button>
                            <Input value={activationUrl} readOnly className="font-mono text-sm" />
                            {enableEmailSending &&
                                email &&
                                userUuid !== undefined &&
                                userUuid.length > 0 && (
                                <Button
                                    onClick={handleSendEmail}
                                    disabled={isSendingEmail}
                                >
                                    {effectiveEmailSent ? 'Resend Email' : 'Send Email'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {showToken && (
                        <div className="space-y-2">
                            <Label>Invitation Token (for reference)</Label>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={handleCopyToken}
                                    title="Copy to clipboard"
                                >
                                    ⧉
                                </Button>
                                <Input
                                    value={invitationToken}
                                    readOnly
                                    className="font-mono text-sm"
                                />
                            </div>
                        </div>
                    )}

                    {actionButtons && <div className="flex gap-2">{actionButtons}</div>}
                </CardContent>
            </Card>

            {enableEmailSending &&
                email &&
                userUuid !== undefined &&
                userUuid.length > 0 && (
                <ConfirmationModal
                    open={showSendEmailConfirm}
                    onOpenChange={setShowSendEmailConfirm}
                    message={`Do you want to ${sendEmailVerb} an account activation email to ${email}?`}
                    onConfirm={handleConfirmSendEmail}
                    confirmLabel={effectiveEmailSent ? 'Resend Email' : 'Send Email'}
                    isLoading={isSendingEmail}
                />
            )}
        </>
    );
}
